import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CITIZEN_FORM, CRAFT } from './CitizenPortrait';
import { QUOTES } from '../data/quotes';

export { QUOTES };

/*
 * The footer's leading edge: a treeline, a house, and a dish that talks to
 * something passing overhead.
 *
 * In normal flow as the footer's first child, so it occupies its own space and
 * can never overlap page content. Decorative throughout -- aria-hidden and
 * pointer-transparent.
 *
 * Depth comes from two silhouette bands: a hazier far ridge behind, the near
 * treeline in the darkest green. Layers step in perceptual lightness so each
 * separates from the one behind it, in both day and night palettes.
 */

const W = 1600;
/*
 * The viewBox reaches above the ground so the band carries its own sky. With
 * it starting at y=0 there were only 24 units above the treetops, and the
 * craft had nowhere to climb -- the open space above was page margin, outside
 * this svg entirely.
 */
const SKY_TOP = -110;
const H = 352;
const GROUND = 150;

/*
 * The yard: open ground between the house line and the near treeline.
 *
 * There was none. The near band started within a couple of units of the house
 * footing, so anything happening at ground level had nowhere to stand and was
 * immediately occluded by foreground trees. Pushing the near band down opens a
 * 36-unit strip that reads as the front garden, which is where the night
 * events play out.
 */
const YARD = GROUND + 36;

/* A picket every 17 units along the left boundary, stopping short of the path. */
const FENCE = Array.from({ length: 11 }, (_, i) => 468 + i * 17);

/* Chimney smoke: three puffs on staggered offsets. */
const SMOKE = [0, 1, 2];

/* Where the dish sits, and where its aperture points. */
const DISH_X = 872;
const DISH_Y = GROUND - 40;

/*
 * The transiting spacecraft's lane. Well clear of the treetops, up in the
 * band's own sky. It still passes behind the near treeline, which is correct:
 * those are foreground.
 */
const SAT_Y = -58;
const SAT_SCALE = 0.85;
const SAT_PERIOD = 34; // seconds for one crossing
const BEAM_REACH = 300; // how close before the link acquires

const LINE_H = 16;
const CLOTH_PAD = 12;

/* Where each cloth hangs relative to the tow point. */
const TOP_Y = -18;
const BY_Y = 20;

/*
 * How far the whole assembly reaches above and below its flight line: the
 * quote cloth above, the name cloth below, both taller than the airframe.
 * Published so the lane it flies in can be checked against the others --
 * the balloon and the biplane were sharing one, 68 units deep into each other.
 */
export const PLANE_SPAN = {
  top: TOP_Y - (2 * LINE_H + CLOTH_PAD) / 2,
  bottom: BY_Y + (LINE_H + CLOTH_PAD) / 2,
  scale: 0.8,
};
/*
 * How wide a character actually runs, per cloth.
 *
 * A monospace face advances about 0.6em, and the tracking on these labels adds
 * to every character on top of that: the quote is 13px on .06em, so 7.8 + .78;
 * the name is 11.5px on .1em, so 6.9 + 1.15. The old figure was a single 7.6
 * for both, which is the 13px advance with the tracking left out -- so the
 * longest line overhung the cloth it was supposed to be printed on. Rounded up,
 * because the fallback stack is not guaranteed to be the metrics above.
 */
const QUOTE_CHAR_W = 8.8;
const BY_CHAR_W = 8.3;

/*
 * The cloth is not all printable. The leading edge carries the batten and its
 * eyelets, and the fly end is forked, so the copy has to clear both -- a cloth
 * padded evenly ran its last characters into the swallowtail.
 */
const LEAD_PAD = 14;
const FLY_PAD = 20;

/*
 * Two cloths, not one. The quote rides above and the name hangs under it on
 * its own line and in its own colour, the way a towed pair actually flies --
 * one banner carrying both put the attribution inside the quotation.
 */
const bannerBox = (lines, charW) => {
  const text = Math.max(...lines.map((line) => line.length)) * charW;
  return {
    text,
    width: LEAD_PAD + text + FLY_PAD,
    height: lines.length * LINE_H + 12,
  };
};

const conifer = (x, base, height, width, key, depth) => {
  const parts = [];
  for (let i = 0; i < 3; i += 1) {
    const t = i / 3;
    const tierTop = base - height * (1 - t * 0.55);
    const tierBase = base - height * (0.34 - t * 0.17) * (2 - i) - height * 0.05;
    const halfWidth = (width / 2) * (0.45 + t * 0.55);
    parts.push(`M ${x} ${tierTop} L ${x + halfWidth} ${tierBase} L ${x - halfWidth} ${tierBase} Z`);
  }
  return (
    <g key={key}>
      <rect className="hz-trunk" x={x - width * 0.06} y={base - height * 0.16} width={width * 0.12} height={height * 0.2} />
      <path className={`hz-tree hz-tree--${depth}`} d={parts.join(' ')} />
    </g>
  );
};

const broadleaf = (x, base, height, width, key, depth) => (
  <g key={key}>
    <rect className="hz-trunk" x={x - width * 0.05} y={base - height * 0.45} width={width * 0.1} height={height * 0.47} />
    <ellipse className={`hz-tree hz-tree--${depth}`} cx={x} cy={base - height * 0.66} rx={width * 0.5} ry={height * 0.36} />
  </g>
);

/* Hand-placed rather than generated: a treeline wants rhythm, not randomness. */
const FAR_TREES = [
  [120, 78, 30], [190, 62, 24], [260, 90, 34], [350, 70, 27], [455, 84, 31],
  [1010, 74, 28], [1105, 92, 34], [1190, 66, 25], [1300, 86, 32], [1430, 72, 27], [1530, 88, 33],
];

const NEAR_TREES = [
  [60, 118, 46], [155, 96, 38], [300, 128, 50], [400, 104, 41],
  [1240, 122, 48], [1360, 100, 40], [1470, 130, 52], [1570, 108, 43],
];

/*
 * The daytime counterpart to the downlink satellite: a biplane crossing the
 * band's sky. It lives here rather than in the fixed viewport layer so it is
 * anchored to the landscape -- it belongs to this scene, not to the scroll.
 *
 * Side elevation, facing right: stacked wings on interplane struts, a swept
 * fin, fixed gear, and a propeller disc with a blade turning inside it.
 */
/*
 * One towed cloth: battened and grommeted at the leading edge where the line
 * pulls, trimmed inside the hem, and cut to a swallowtail at the fly end. The
 * fork is the part that does the work -- a plain rectangle reads as a sign held
 * up behind an aircraft, and a forked one reads as fabric under tension.
 */
const Cloth = ({ box, y, lead, dir, tone, delay, children }) => {
  const fly = lead + dir * box.width;
  const half = box.height / 2;
  const notch = 10;
  const batten = lead + dir * 1.5;
  const hem = 4;

  return (
    <g className="hz-cloth" style={{ animationDelay: `${delay}s` }}>
      <path
        className={`hz-banner-cloth${tone ? ` ${tone}` : ''}`}
        d={`M ${lead} ${y - half}
            L ${fly} ${y - half}
            L ${fly - dir * notch} ${y}
            L ${fly} ${y + half}
            L ${lead} ${y + half} Z`}
      />
      {/* Hem lines, stopping short of the fork. */}
      <path
        className="hz-banner-trim"
        d={`M ${lead + dir * hem} ${y - half + hem} L ${fly - dir * (notch + 2)} ${y - half + hem}`}
      />
      <path
        className="hz-banner-trim"
        d={`M ${lead + dir * hem} ${y + half - hem} L ${fly - dir * (notch + 2)} ${y + half - hem}`}
      />
      {/* The batten the line pulls against, and the eyelets along it. */}
      <rect
        className="hz-banner-batten"
        x={dir > 0 ? batten - 1.6 : batten - 1.6}
        y={y - half}
        width={3.2}
        height={box.height}
        rx={1.4}
      />
      {[-0.62, 0, 0.62].map((at) => (
        <circle key={at} className="hz-banner-eyelet" cx={batten} cy={y + at * half} r={1.1} />
      ))}
      {children}
    </g>
  );
};

const Biplane = ({ quote, heading, onPass }) => {
  const west = heading === 'west';
  const top = bannerBox(quote.text, QUOTE_CHAR_W);
  const by = quote.by ? bannerBox([quote.by], BY_CHAR_W) : null;

  /*
   * The banners trail, so which side they hang on depends on which way the
   * aircraft is pointing: off the tail to the left going east, to the right
   * going west. Mirroring the whole group instead would have flipped the text.
   */
  const tail = west ? 34 : -34;
  const hitch = west ? 52 : -52;
  // Both cloths hang from the same hitch and run outward from it.
  const dir = west ? 1 : -1;
  /*
   * Centred on the printable panel, not on the cut shape: the batten end and
   * the forked end take different bites out of the cloth, so the midpoint of
   * the whole thing is not the midpoint of the space the copy has.
   */
  const middle = (box) => hitch + dir * (LEAD_PAD + box.text / 2);

  return (
    <g className={`hz-plane is-${heading}`} onAnimationIteration={onPass}>
      {/*
        * Outside the bob group, so the cloths trail level while the aircraft
        * pitches -- a banner bobbing with the tail would read as rigid.
        */}
      <g className="hz-banner" style={{ transformOrigin: `${tail}px 0` }}>
        <line className="hz-banner-line" x1={tail} y1={0} x2={hitch} y2={TOP_Y} />
        <Cloth box={top} y={TOP_Y} lead={hitch} dir={dir} delay={0}>
          <text className="hz-banner-text" textAnchor="middle">
            {quote.text.map((line, i) => (
              <tspan
                key={line}
                x={middle(top)}
                y={TOP_Y - ((quote.text.length - 1) * LINE_H) / 2 + i * LINE_H + 5}
              >
                {line}
              </tspan>
            ))}
          </text>
        </Cloth>

        {by && (
          <>
            <line className="hz-banner-line" x1={tail} y1={0} x2={hitch} y2={BY_Y} />
            <Cloth box={by} y={BY_Y} lead={hitch} dir={dir} tone="is-by" delay={-1.6}>
              <text className="hz-banner-text is-by" textAnchor="middle">
                <tspan x={middle(by)} y={BY_Y + 5}>
                  {quote.by}
                </tspan>
              </text>
            </Cloth>
          </>
        )}
      </g>

      {/* Only the airframe turns; the banner has already been placed to suit. */}
      <g transform={west ? 'scale(-1 1)' : undefined}>
        <g className="hz-plane-bob">
          <path className="hz-plane-body" d="M -25 -3 L -32 -16 L -22 -16 L -15 -3 Z" />
          <rect className="hz-plane-body" x={-34} y={-2.4} width={13} height={3} rx={1.5} />
          <line className="hz-plane-strut" x1={4} y1={8} x2={1} y2={14} />
          <line className="hz-plane-strut" x1={12} y1={8} x2={15} y2={14} />
          <circle className="hz-plane-wheel" cx={0} cy={15.5} r={3} />
          <circle className="hz-plane-wheel" cx={16} cy={15.5} r={3} />
          <path
            className="hz-plane-body"
            d="M 27 0 Q 25 -5 13 -6 L -19 -4.5 Q -26 -3.5 -26 0 Q -26 3.5 -19 4.5 L 13 6 Q 25 5 27 0 Z"
          />
          <path className="hz-plane-glass" d="M 4 -6 Q 8 -11 14 -6 Z" />
          <line className="hz-plane-strut" x1={-2} y1={-6} x2={-3} y2={-17} />
          <line className="hz-plane-strut" x1={14} y1={-6} x2={15} y2={-17} />
          <line className="hz-plane-strut" x1={7} y1={-6} x2={7} y2={-17} />
          <rect className="hz-plane-wing" x={-9} y={6.2} width={32} height={3.4} rx={1.7} />
          <rect className="hz-plane-wing" x={-11} y={-19} width={36} height={3.6} rx={1.8} />
          <ellipse className="hz-plane-disc" cx={28} cy={0} rx={2.2} ry={11} />
          <g className="hz-prop">
            <rect x={27.2} y={-11} width={1.6} height={22} rx={0.8} />
          </g>
        </g>
      </g>
    </g>
  );
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
};

const Horizon = () => {
  const reduced = usePrefersReducedMotion();
  // A fresh quote each pass, and the opposite heading to the one before.
  const [pass, setPass] = useState(0);

  /*
   * Animation events bubble, and this group is full of them: the propeller
   * alone completes a turn every 0.22s, so its iterations were reaching this
   * handler about four times a second. The result was the aircraft flipping
   * heading and quote on every frame -- read from outside as two biplanes
   * flashing against each other.
   *
   * Only the crossing counts as a pass, so anything that did not fire on this
   * element is somebody else's animation finishing a lap.
   */
  const nextPass = (event) => {
    if (event.target !== event.currentTarget) return;
    setPass((n) => n + 1);
  };
  const satRef = useRef(null);
  const spinRef = useRef(null);
  const beamRef = useRef(null);
  const pulseRefs = useRef([]);

  /*
   * The satellite and its beam are driven together in one loop. A CSS
   * animation could move the craft, but the beam has to be redrawn each frame
   * to stay anchored between a moving transmitter and a fixed dish -- that is
   * geometry CSS cannot express.
   */
  useLayoutEffect(() => {
    const sat = satRef.current;
    const beam = beamRef.current;
    if (!sat || !beam) return undefined;

    const place = (elapsed) => {
      const t = (elapsed % SAT_PERIOD) / SAT_PERIOD;
      const x = -120 + t * (W + 240);
      sat.setAttribute('transform', `translate(${x.toFixed(1)} ${SAT_Y})`);
      if (spinRef.current) {
        spinRef.current.setAttribute(
          'transform',
          `rotate(${((elapsed * 9) % 360).toFixed(1)}) scale(${SAT_SCALE})`,
        );
      }

      // The link acquires as the craft comes within reach and fades as it goes.
      const offset = Math.abs(x - DISH_X);
      const strength = Math.max(0, 1 - offset / BEAM_REACH);
      beam.style.opacity = (strength * strength * 0.7).toFixed(3);
      if (strength > 0) {
        beam.setAttribute(
          'points',
          `${x.toFixed(1)},${SAT_Y + 5} ${DISH_X - 11},${DISH_Y - 4} ${DISH_X + 7},${DISH_Y + 7}`,
        );
        // Packets running down the beam.
        pulseRefs.current.forEach((pulse, index) => {
          if (!pulse) return;
          const phase = ((elapsed * 0.65 + index / pulseRefs.current.length) % 1);
          pulse.setAttribute('cx', (x + (DISH_X - x) * phase).toFixed(1));
          pulse.setAttribute('cy', (SAT_Y + 5 + (DISH_Y - SAT_Y - 5) * phase).toFixed(1));
          pulse.style.opacity = (strength * (1 - Math.abs(phase - 0.5) * 1.2)).toFixed(3);
        });
      }
    };

    if (reduced) {
      place(SAT_PERIOD * 0.5); // parked mid-pass, link established
      return undefined;
    }
    let frame;
    const started = performance.now();
    const tick = (now) => {
      place((now - started) / 1000);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reduced]);

  return (
    <div className="horizon" aria-hidden="true">
      <svg viewBox={`0 ${SKY_TOP} ${W} ${H}`} preserveAspectRatio="xMidYMax slice" focusable="false">
        {/* Far ridge. */}
        <path
          className="hz-ground hz-ground--far"
          d={`M 0 ${GROUND - 6} Q 400 ${GROUND - 26} 820 ${GROUND - 12} T 1600 ${GROUND - 20} L 1600 ${SKY_TOP + H} L 0 ${SKY_TOP + H} Z`}
        />
        {FAR_TREES.map(([x, h, w], i) => conifer(x, GROUND - 4, h, w, `f${i}`, 'far'))}
        {broadleaf(540, GROUND - 4, 74, 66, 'fb1', 'far')}
        {broadleaf(1390, GROUND - 4, 68, 60, 'fb2', 'far')}

        <Biplane
          quote={QUOTES[pass % QUOTES.length]}
          heading={pass % 2 === 0 ? 'east' : 'west'}
          onPass={nextPass}
        />

        {/* The downlink, and the craft carrying it. */}
        <g className="hz-downlink">
          <polygon ref={beamRef} className="hz-beam" points="0,0 0,0 0,0" />
          {[0, 1, 2].map((i) => (
            <circle
              key={i}
              ref={(node) => {
                pulseRefs.current[i] = node;
              }}
              className="hz-pulse"
              r={1.6}
            />
          ))}
          <g ref={satRef}>
            <g ref={spinRef} transform={`scale(${SAT_SCALE})`}>
              {/* Bus, wings, and a steerable dish. */}
              <rect className="hz-sat-bus" x={-7} y={-5} width={14} height={10} rx={1.6} />
              <rect className="hz-sat-panel" x={-26} y={-3.4} width={16} height={6.8} rx={1} />
              <rect className="hz-sat-panel" x={10} y={-3.4} width={16} height={6.8} rx={1} />
              <line className="hz-sat-strut" x1={-10} y1={0} x2={-7} y2={0} />
              <line className="hz-sat-strut" x1={7} y1={0} x2={10} y2={0} />
              <path className="hz-sat-dish" d="M -5 6 A 6 6 0 0 0 5 6 Z" />
              <line className="hz-sat-strut" x1={0} y1={5} x2={0} y2={9} />
              <circle className="hz-sat-beacon" cx={0} cy={-7} r={1.4} />
            </g>
          </g>
        </g>

        {/* The daytime balloon passes behind the house, like distant traffic. */}
        <g className="hz-day hz-day-crossing">
          <g className="hz-balloon">
            <g className="hz-balloon-lift">
              <g transform="scale(1.9)">
                {CRAFT.ux.parts.map((part, i) => (
                  <path key={i} className={part.line ? 'hz-craft-line' : 'hz-craft'} d={part.d} />
                ))}
                {(CRAFT.ux.glass ?? []).map((pane, i) => (
                  <path key={`g${i}`} className="hz-craft-glass" d={pane} />
                ))}
              </g>
            </g>
          </g>
        </g>

        {/* The house. */}
        <g className="hz-house">
          {/* Two storeys, with the gable end facing the road. */}
          <rect x={668} y={GROUND - 58} width={152} height={58} />
          <path className="hz-house-side" d={`M 668 ${GROUND - 58} L 704 ${GROUND - 58} L 704 ${GROUND} L 668 ${GROUND} Z`} />
          <rect className="hz-house-foundation" x={658} y={GROUND - 3} width={172} height={7} rx={1} />
          {/* Chimney sits behind the roof plane, so its base disappears into the slope. */}
          <rect x={778} y={GROUND - 134} width={18} height={74} />
          <rect x={773} y={GROUND - 139} width={28} height={7} rx={1.5} />
          <path className="hz-chimney-line" d={`M 781 ${GROUND - 122} L 793 ${GROUND - 122} M 781 ${GROUND - 113} L 793 ${GROUND - 113}`} />
          <path className="hz-roof" d={`M 646 ${GROUND - 55} L 744 ${GROUND - 110} L 842 ${GROUND - 55} L 826 ${GROUND - 55} L 744 ${GROUND - 101} L 662 ${GROUND - 55} Z`} />
          <path className="hz-roof-face" d={`M 660 ${GROUND - 57} L 744 ${GROUND - 103} L 828 ${GROUND - 57} Z`} />
          <path className="hz-siding" d={`M 672 ${GROUND - 9} H 730 M 762 ${GROUND - 9} H 816 M 672 ${GROUND - 18} H 730 M 762 ${GROUND - 18} H 816`} />
          {/* Porch: roof, posts, and a railing broken for the steps. */}
          <rect x={676} y={GROUND - 37} width={140} height={4} rx={2} />
          <rect x={681} y={GROUND - 33} width={4} height={33} />
          <rect x={807} y={GROUND - 33} width={4} height={33} />
          <rect x={685} y={GROUND - 19} width={41} height={3} rx={1.5} />
          <rect x={770} y={GROUND - 19} width={37} height={3} rx={1.5} />
          {/* Steps down into the yard, widening toward the viewer. */}
          <rect x={726} y={GROUND} width={44} height={5} rx={1} />
          <rect x={721} y={GROUND + 5} width={54} height={5} rx={1} />
          <rect x={716} y={GROUND + 10} width={64} height={5} rx={1} />
        </g>

        {/* The path, widening as it comes forward. */}
        <path className="hz-path" d={`M 716 ${GROUND + 15} L 780 ${GROUND + 15} L 806 ${YARD} L 690 ${YARD} Z`} />

        {/* Fence along the left boundary, and the mailbox at its end. */}
        <g className="hz-fence">
          <rect x={462} y={GROUND + 18} width={196} height={2.5} />
          <rect x={462} y={GROUND + 27} width={196} height={2.5} />
          {FENCE.map((x) => (
            <path key={x} d={`M ${x} ${GROUND + 12} L ${x + 5} ${GROUND + 15} L ${x + 5} ${GROUND + 34} L ${x} ${GROUND + 34} Z`} />
          ))}
          <rect x={444} y={GROUND + 8} width={4} height={26} />
          <rect x={434} y={GROUND + 2} width={24} height={11} rx={2.5} />
        </g>

        <rect className="hz-door" x={734} y={GROUND - 33} width={26} height={33} rx={1} />
        <rect className="hz-door-panel" x={738} y={GROUND - 28} width={18} height={10} rx={1} />
        <rect className="hz-door-panel" x={738} y={GROUND - 15} width={18} height={10} rx={1} />
        <circle className="hz-door-knob" cx={753} cy={GROUND - 16} r={1.2} />
        <rect className="hz-window" x={690} y={GROUND - 50} width={28} height={21} rx={1.5} />
        <rect className="hz-window" x={778} y={GROUND - 50} width={28} height={21} rx={1.5} />
        <rect className="hz-window hz-window--dim" x={730} y={GROUND - 92} width={22} height={17} rx={1.5} />
        <path className="hz-window-frame" d={`M 704 ${GROUND - 50} V ${GROUND - 29} M 690 ${GROUND - 39.5} H 718 M 792 ${GROUND - 50} V ${GROUND - 29} M 778 ${GROUND - 39.5} H 806 M 741 ${GROUND - 92} V ${GROUND - 75} M 730 ${GROUND - 83.5} H 752`} />
        <circle className="hz-porch-light" cx={800} cy={GROUND - 30} r={2} />

        {/* Smoke, which is the only sign the house is occupied by day. */}
        <g className="hz-smoke">
          {SMOKE.map((i) => (
            <circle key={i} cx={801} cy={GROUND - 142} r={4.5} style={{ animationDelay: `${i * 2.6}s` }} />
          ))}
        </g>

        {/*
          * Daytime.
          *
          * The counterpart to the night watch, and deliberately uneventful.
          * The warden walks the fence, checks the mailbox and goes back in;
          * nothing happens, which is what a good day looks like. Security is
          * the one culture on duty in both skies, doing the same job two ways.
          */}
        <g className="hz-day hz-day-yard">
          {/* The warden's round: out to the mailbox, along the fence, back. */}
          <g className="hz-patrol">
            <g className="hz-warden" transform="scale(0.92)">
              {CITIZEN_FORM.sec.parts.map((part, i) => (
                <path key={i} className={part.line ? 'hz-warden-line' : undefined} d={part.d} />
              ))}
            </g>
          </g>

          {/* A technician walks the dish's perimeter and checks the signal hardware. */}
          <g className="hz-rigger" transform={`translate(${DISH_X - 13} ${GROUND - 2})`}>
            <g transform="scale(0.72)">
              {CITIZEN_FORM.arch.parts.map((part, i) => (
                <path key={i} className={part.line ? 'hz-warden-line' : undefined} d={part.d} />
              ))}
            </g>
            <path className="hz-rigger-tool" d="M 8 -20 L 17 -25" />
          </g>
        </g>

        {/*
          * Night watch.
          *
          * Something comes up the fence line for the house, and a warden walks
          * out to meet it. The warden is the same body plan that stands on the
          * Security culture card -- one people, wherever they turn up -- and
          * the outcome is not in question: the intruder never reaches the path.
          *
          * Dark theme only, and only when motion is welcome.
          */}
        <g className="hz-night">
          <g className="hz-intruder">
            <path d="M 0 0 L -10 0 C -11.5 -9 -8 -18 -1.5 -20 C 4.5 -21.5 8 -16.5 6.5 -10 L 5 0 Z" />
            <circle cx={1} cy={-23} r={4.2} />
          </g>

          <g className="hz-warden-group">
            <path className="hz-scan" d={`M 0 -16 L -96 -44 L -96 10 Z`} />
            <g className="hz-warden">
              {CITIZEN_FORM.sec.parts.map((part, i) => (
                <path key={i} className={part.line ? 'hz-warden-line' : undefined} d={part.d} />
              ))}
            </g>
          </g>
        </g>

        {/* Ground station. */}
        <g className="hz-dish">
          <rect className="hz-dish-mast" x={DISH_X - 2} y={DISH_Y + 6} width={4} height={34} />
          <rect className="hz-dish-mast" x={DISH_X - 9} y={GROUND - 3} width={18} height={4} rx={1} />
          <g transform={`translate(${DISH_X} ${DISH_Y}) rotate(-32)`}>
            <ellipse className="hz-dish-face" cx={0} cy={0} rx={17} ry={7} />
            <ellipse className="hz-dish-rim" cx={0} cy={-1.6} rx={17} ry={7} />
            <line className="hz-dish-arm" x1={0} y1={-1} x2={0} y2={-11} />
            <circle className="hz-dish-feed" cx={0} cy={-12} r={2.2} />
          </g>
        </g>

        {/* Near treeline and ground. */}
        {NEAR_TREES.map(([x, h, w], i) => conifer(x, YARD + 4, h, w, `n${i}`, 'near'))}
        {broadleaf(950, YARD + 4, 96, 84, 'nb1', 'near')}
        <path
          className="hz-ground hz-ground--near"
          d={`M 0 ${YARD + 2} Q 380 ${YARD - 10} 760 ${YARD + 4} T 1600 ${YARD - 2} L 1600 ${SKY_TOP + H} L 0 ${SKY_TOP + H} Z`}
        />
      </svg>
    </div>
  );
};

export default Horizon;
